/**
 * Language Selector Component
 * Allows users to switch between supported languages
 */

'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 text-muted-foreground bg-transparent hover:bg-transparent flex items-center gap-1.5"
        aria-label="Select language"
        title="Select language"
      >
        <Globe size={18} />
        <span className="text-xs font-medium">{currentLanguage.flag}</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 z-50 bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden min-w-[160px] animate-message-slide-up">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-muted transition-colors ${
                  lang.code === i18n.language ? 'bg-muted' : ''
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm font-medium text-foreground">{lang.name}</span>
                {lang.code === i18n.language && (
                  <span className="ml-auto text-xs text-muted-foreground">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
